import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import useRetryFetch from "../../hooks/useRetryFetch";
import {
  abandonNow, bringNow, finishNow, handoffNow, startNow,
  type NowHandoffRoute, type NowLinkKind, type NowMode, type NowSessionDto,
} from "../../api/now";
import { followUpTarget } from "../../lib/nowModes";
import { stageMmOpen } from "../../lib/mmHandoff";
import { stageWorkspaceItem } from "../../lib/workspaceHandoff";
import { trackCapture } from "../../lib/captureAnalytics";
import { questionKey, routeKey, type DeeperItem, type DeeperRoute } from "../../lib/diveDeeper";
import { isInlineRoute, runHandoff } from "../../lib/deeperRunner";
import ConnectionCard from "./ConnectionCard";
import DiveChoices from "./DiveChoices";
import NowTake from "./NowTake";
import NowToolHost, { type ToolOutcome } from "./NowToolHost";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";

type Screen = "dive" | "tool" | "finishing" | "take" | "error";
const MAX_BROUGHT = 2;
const bringKey = (kind: string, id: string) => `${kind}:${id}`;
const FADE = "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200";

interface Props {
  captureId: string;
  mode: NowMode | null;
  item: DeeperItem;
  reflective: boolean;
  onClose: () => void;
}

/**
 * Dive deeper (spec 2026-11-04 §2.3–2.5): one screen — what you already have, the guiding
 * question, the main choices, "Short on time?", "More ways" — then the tool, then the take screen.
 * The note is already saved when this opens.
 */
const NowSession = ({ captureId, mode, item, reflective, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>("dive");
  const [session, setSession] = useState<NowSessionDto | null>(null);
  const [startFailed, setStartFailed] = useState(false);
  const [tool, setTool] = useState<DeeperRoute | null>(null);
  const [opening, setOpening] = useState(false);
  const [runFailed, setRunFailed] = useState(false);
  const [brought, setBrought] = useState<string[]>([]);
  const finished = useRef(false);
  const handedOff = useRef(false);
  const sessionId = useRef<string | null>(null);
  const started = useRef(false);
  /** Resolves with the session, or null when the start failed (a tapped card waits on it). */
  const startPromise = useRef<Promise<NowSessionDto | null> | null>(null);
  const startPending = useRef(true);
  const alive = useRef(true);
  const heading = useRef<HTMLHeadingElement>(null);
  const qKey = questionKey(item, reflective);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const lang = (i18n.language || "en").slice(0, 2);
    const locale = (["en", "he", "de"].includes(lang) ? lang : "en") as "en" | "he" | "de";
    startPromise.current = startNow(retryFetch, { captureId, ...(mode ? { mode } : {}), itemRef: item, reflective, locale })
      .then(({ session: s }): NowSessionDto | null => {
        sessionId.current = s._id;
        // Closed before the session answered: nothing will use it, so don't leave it open.
        if (!alive.current) {
          abandonNow(retryFetch, s._id).catch(() => {});
          return null;
        }
        setSession(s);
        setBrought(s.broughtIn.map((b) => bringKey(b.kind, b.id)));
        return s;
      })
      .catch(() => {
        if (alive.current) setStartFailed(true);
        return null;
      })
      .finally(() => {
        startPending.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate, unguarded effect so StrictMode's dev remount re-registers the cleanup.
  // A handed-off session stays open for the resume card.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (sessionId.current && !finished.current && !handedOff.current) abandonNow(retryFetch, sessionId.current).catch(() => {});
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Each screen change moves focus to its heading (spec §6).
  useEffect(() => {
    if (screen !== "tool") heading.current?.focus();
  }, [screen]);

  const leave = (path: string) => {
    onClose();
    navigate(path);
  };

  /** runHandoff's beforeLeave: never throws, so a failed call still navigates (Ruling 14). */
  const recordHandoff = async (route: string) => {
    if (!sessionId.current) return;
    handedOff.current = true;
    try {
      await handoffNow(retryFetch, sessionId.current, { route: route as NowHandoffRoute });
    } catch {
      // Still go: the person asked to. The server has no hand-off, so let unmount abandon it.
      handedOff.current = false;
    }
  };

  const run = async (route: DeeperRoute) => {
    setRunFailed(false);
    trackCapture("capture_deeper_opened", { kind: item.kind, route: routeKey(route) });
    // Tapped before /api/now/start answered: wait (busy), so the tool never runs against an unknown session.
    if (startPending.current && startPromise.current) {
      setOpening(true);
      await startPromise.current;
      if (!alive.current) return;
    }
    if (isInlineRoute(route)) {
      setOpening(false);
      setTool(route);
      setScreen("tool");
      return;
    }
    setOpening(true);
    try {
      await runHandoff(route, item, { retryFetch, beforeLeave: recordHandoff, leave });
    } catch {
      handedOff.current = false;
      setRunFailed(true);
      setOpening(false);
    }
  };

  const openConnection = async (kind: NowLinkKind, id: string) => {
    if (opening) return;
    const target = followUpTarget(kind, id);
    setOpening(true);
    await recordHandoff("connection");
    if (target.mmId) stageMmOpen(target.mmId);
    if (target.workspace) stageWorkspaceItem(target.workspace.section, target.workspace.id);
    leave(target.path);
  };

  const toggleBring = async (kind: NowLinkKind, id: string) => {
    if (!session) return;
    const k = bringKey(kind, id);
    const on = !brought.includes(k);
    if (on && brought.length >= MAX_BROUGHT) return;
    const prev = brought;
    setBrought(on ? [...brought, k] : brought.filter((x) => x !== k));
    try {
      const r = await bringNow(retryFetch, session._id, { kind, id, on });
      setBrought(r.broughtIn.map((b) => bringKey(b.kind, b.id)));
    } catch {
      setBrought(prev);
    }
  };

  const finishTool = async (out: ToolOutcome) => {
    if (!tool) return;
    // Without a session the main cards still ran today's handlers; there is nothing to finish.
    const id = sessionId.current;
    if (!id) {
      onClose();
      return;
    }
    setScreen("finishing");
    try {
      const { session: done } = await finishNow(retryFetch, id, {
        tool: routeKey(tool),
        ...(out.result ? { result: out.result } : {}),
        ...(out.answer ? { answer: out.answer } : {}),
      });
      finished.current = true;
      setSession(done);
      setScreen("take");
    } catch {
      setScreen("error");
    }
  };

  if (screen === "finishing") {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-foreground/80" role="status">
        <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
        <h2 ref={heading} tabIndex={-1} className="text-sm font-normal focus:outline-none">{t("now.finishing")}</h2>
      </div>
    );
  }

  if (screen === "error") {
    return (
      <div className="space-y-4">
        <div role="alert">
          <h2 ref={heading} tabIndex={-1} className="text-sm font-normal text-foreground/80 focus:outline-none">{t("now.error")}</h2>
        </div>
        <div className="flex justify-end">
          <Button className="rounded-full min-h-11" onClick={onClose}>{t("now.done")}</Button>
        </div>
      </div>
    );
  }

  if (screen === "take" && session) {
    return (
      <div className={FADE}>
        <NowTake session={session} onClose={onClose} headingRef={heading} />
      </div>
    );
  }

  if (screen === "tool" && tool) {
    return (
      <div className={FADE}>
        <NowToolHost
          route={tool}
          item={item}
          mode={session?.mode ?? mode ?? "respond"}
          question={t(`now.dive.question.${qKey}`)}
          onDone={finishTool}
          onHandoff={async (route) => {
            // The tool navigates itself; record it like any hand-off, then let the sheet close.
            await recordHandoff(route);
            onClose();
          }}
          onBack={() => {
            setTool(null);
            setScreen("dive");
          }}
        />
      </div>
    );
  }

  return (
    <div className={`space-y-5 ${FADE}`}>
      <section className="space-y-1" aria-labelledby="now-have">
        <p id="now-have" className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          {t("now.dive.haveTitle")}
        </p>
        {startFailed ? (
          <p className="text-sm text-foreground/80" role="status">{t("now.dive.loadFailed")}</p>
        ) : !session ? (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-9 w-full rounded-lg" />
            <Skeleton className="h-9 w-full rounded-lg" />
          </div>
        ) : session.connections.length === 0 ? (
          <p className="text-sm text-foreground/80">{t("now.dive.newThread")}</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {session.connections.map((c) => {
              const k = bringKey(c.kind, c.id);
              const on = brought.includes(k);
              return (
                <ConnectionCard
                  key={k}
                  connection={c}
                  brought={on}
                  canBring={on || brought.length < MAX_BROUGHT}
                  busy={opening}
                  onToggle={() => toggleBring(c.kind, c.id)}
                  onOpen={() => openConnection(c.kind, c.id)}
                />
              );
            })}
          </ul>
        )}
      </section>

      <DiveChoices
        headingRef={heading}
        item={item}
        reflective={reflective}
        questionKey={qKey}
        session={session}
        failed={startFailed}
        busy={opening}
        onRun={run}
      />

      {opening && (
        <p role="status" className="flex items-center gap-2 text-sm text-foreground/80">
          <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
          {t("now.dive.opening")}
        </p>
      )}

      {runFailed && <p role="alert" className="text-sm text-destructive">{t("now.dive.failed")}</p>}

      <div className="flex justify-end">
        <Button variant="ghost" className="min-h-11" onClick={onClose}>{t("now.dive.close")}</Button>
      </div>
    </div>
  );
};

export default NowSession;
